import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { fetchAllowedMessageTypes } from '../services/allowedMessageTypesService';
import { useAuth } from '../context/AuthContext';
// Keep as fallback in case API fails
import messageTypes from '../Assets/messageTypes.json'; // XML message types
import messageTypesNonXML from '../Assets/messageTypesNonXML.json'; // Non-XML message types
import '../styles/MessageTypes.scss';

interface MessageTypeDropdownProps {
  value: string;
  onChange: (value: string) => void;
  isXml: 0 | 1; // Determines whether to load XML or non-XML message types
}

const MessageTypeDropdown: React.FC<MessageTypeDropdownProps> = ({ value, onChange, isXml }) => {
  const [currentMessageTypes, setCurrentMessageTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const userGroups = useAuth();
  const accessToken = userGroups?.userData?.access_token || '';

  // Function to fetch and update message types
  const fetchAndUpdateMessageTypes = async (isBackground = false) => {
    try {
      if (isBackground) setIsPolling(true);
      
      const apiMessageTypes = await fetchAllowedMessageTypes(isXml, accessToken);
      
      // Only update if there are new message types
      setCurrentMessageTypes(prevTypes => {
        const newTypes = apiMessageTypes.filter(type => !prevTypes.includes(type));
        if (newTypes.length > 0) {
          const updatedList = [...new Set([...prevTypes, ...apiMessageTypes])].sort((a, b) => {
            if (!a || !b) return 0;
            return a.localeCompare(b);
          });
          // console.log(`${isBackground ? 'Background' : 'Manual'} update: Found ${newTypes.length} new message types:`, newTypes);
          // console.log(`Total message types after update: ${updatedList.length}`);
          setLastUpdated(new Date());
          return updatedList;
        }
        return prevTypes;
      });
    } catch (error) {
      console.error(`${isBackground ? 'Background' : 'Manual'} update failed to fetch message types from API:`, error);
    } finally {
      if (isBackground) setIsPolling(false);
    }
  };

  useEffect(() => {
    const loadMessageTypes = async () => {
      setLoading(true);
      try {
        // Try to fetch from API first
        const apiMessageTypes = await fetchAllowedMessageTypes(isXml, accessToken);
        setCurrentMessageTypes(apiMessageTypes);
        setLastUpdated(new Date());
        // console.log(`Initial load: Loaded ${apiMessageTypes.length} message types from API (isXml: ${isXml})`);
      } catch (error) {
        console.error('Failed to fetch message types from API, falling back to static files:', error);
        // Fallback to static JSON files if API fails
        setCurrentMessageTypes(isXml === 1 ? messageTypes : messageTypesNonXML);
      } finally {
        setLoading(false);
      }
    };

    loadMessageTypes();
  }, [isXml, accessToken]);

  // Background polling effect
  useEffect(() => {
    if (!accessToken) return;

    // console.log('Starting background polling for message types every 10 seconds');
    
    const interval = setInterval(() => {
      //console.log('Background polling: Checking for new message types...');
      fetchAndUpdateMessageTypes(true);
    }, 10000); // 10 seconds

    // Cleanup interval on component unmount or when dependencies change
    return () => {
      // console.log('Stopping background polling for message types');
      clearInterval(interval);
    };
  }, [isXml, accessToken]);

  // Create the label text with polling status
  const getLabelText = () => {
    let baseLabel = "Search by Message Type";
    if (loading) return "Loading Message Types...";
    if (isPolling) return "Updating Message Types...";
    if (lastUpdated) {
      const timeString = lastUpdated.toLocaleTimeString();
      return `${baseLabel} (Updated: ${timeString})`;
    }
    return baseLabel;
  };

  return (
    <div className="messagetype-dropdown">
      <Autocomplete
        disablePortal
        options={currentMessageTypes}
        value={value}
        inputValue={value}
        freeSolo
        forcePopupIcon={true} // Force the dropdown icon to show
        onChange={(_, newValue) => {
          onChange(newValue || '');
        }}
        onInputChange={(_, newInputValue) => {
          onChange(newInputValue || '');
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={getLabelText()}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              classes: {
                input: 'ellipsisInput',
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                border: 'none !important',
                outline: 'none !important',
                '& fieldset': { 
                  display: 'none !important',
                },
                '&:hover fieldset': { 
                  display: 'none !important',
                },
                '&.Mui-focused fieldset': { 
                  display: 'none !important',
                },
                '&.Mui-disabled fieldset': { 
                  display: 'none !important',
                },
              },
              '& .MuiInputBase-root': {
                border: 'none !important',
                outline: 'none !important',
              },
              '& .MuiInputLabel-root': {
                color: 'white',
                fontSize: '1rem',
                fontWeight: 'bold',
              },
              '& .MuiSvgIcon-root': { color: 'white' },
            }}
          />
        )}

      />
    </div>
  );
};

export default MessageTypeDropdown;
